/**
 * Shared profile completeness rules for the hub cards and subpage “Required” badges.
 * Field sets match guest booking validation (GuestValidation) so hub status reflects
 * what is actually needed before a trip.
 */

export type ProfileCompletenessData = {
  fullName: string;
  gender: string;
  email: string;
  phone: string;
  countryOfIssuance: string;
  passportNumber: string;
  expiryDate: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  certificateLevel: string;
  certifyingAgency: string;
  certificateNumber: string;
};

export type ProfileSection =
  | 'basicPersonalInfo'
  | 'travelDocuments'
  | 'diveCertificate'
  | 'diveEquipment';

export type ProfileField = keyof ProfileCompletenessData;

export const PROFILE_SECTION_FIELDS: Record<
  ProfileSection,
  readonly ProfileField[]
> = {
  basicPersonalInfo: ['fullName', 'gender', 'email', 'phone'],
  travelDocuments: [
    'countryOfIssuance',
    'passportNumber',
    'expiryDate',
    'emergencyContactName',
    'emergencyContactPhone',
  ],
  diveCertificate: [
    'certificateLevel',
    'certifyingAgency',
    'certificateNumber',
  ],
  /** Optional on hub — sizes only matter when renting. */
  diveEquipment: [],
};

export const PROFILE_FIELD_LABELS: Record<ProfileField, string> = {
  fullName: 'full name',
  gender: 'gender',
  email: 'email',
  phone: 'phone number',
  countryOfIssuance: 'passport country of issuance',
  passportNumber: 'passport number',
  expiryDate: 'passport expiry date',
  emergencyContactName: 'emergency contact name',
  emergencyContactPhone: 'emergency contact phone',
  certificateLevel: 'dive certificate level',
  certifyingAgency: 'certifying agency',
  certificateNumber: 'certificate number',
};

export type RequiredFieldInfo = {
  fieldName: ProfileField;
  message: string;
};

export const isProfileFieldFilled = (
  value: string | null | undefined,
): boolean => Boolean(value?.trim());

export const findFirstMissingField = (
  data: ProfileCompletenessData,
  requiredFields: readonly ProfileField[],
): RequiredFieldInfo | null => {
  for (const field of requiredFields) {
    if (!isProfileFieldFilled(data[field])) {
      return {
        fieldName: field,
        message: `Missing required ${PROFILE_FIELD_LABELS[field]}`,
      };
    }
  }
  return null;
};

export const findFirstMissingSectionField = (
  data: ProfileCompletenessData,
  section: ProfileSection,
): RequiredFieldInfo | null =>
  findFirstMissingField(data, PROFILE_SECTION_FIELDS[section]);

export const isSectionComplete = (
  data: ProfileCompletenessData,
  section: ProfileSection,
): boolean => findFirstMissingSectionField(data, section) === null;

export const isBasicPersonalInfoFieldRequired = (
  field: ProfileField,
): boolean =>
  (PROFILE_SECTION_FIELDS.basicPersonalInfo as readonly string[]).includes(
    field,
  );

export const isPassportComplete = (
  data: Pick<
    ProfileCompletenessData,
    'countryOfIssuance' | 'passportNumber' | 'expiryDate'
  >,
): boolean =>
  isProfileFieldFilled(data.countryOfIssuance) &&
  isProfileFieldFilled(data.passportNumber) &&
  isProfileFieldFilled(data.expiryDate);

export const isEmergencyContactComplete = (
  data: Pick<
    ProfileCompletenessData,
    'emergencyContactName' | 'emergencyContactPhone'
  >,
): boolean =>
  isProfileFieldFilled(data.emergencyContactName) &&
  isProfileFieldFilled(data.emergencyContactPhone);

export const isDiveCertificateComplete = (
  data: Pick<
    ProfileCompletenessData,
    'certificateLevel' | 'certifyingAgency' | 'certificateNumber'
  >,
): boolean =>
  isProfileFieldFilled(data.certificateLevel) &&
  isProfileFieldFilled(data.certifyingAgency) &&
  isProfileFieldFilled(data.certificateNumber);

/** Required sections that contribute to booking readiness (equipment excluded). */
export const READINESS_SECTIONS: readonly ProfileSection[] = [
  'basicPersonalInfo',
  'travelDocuments',
  'diveCertificate',
] as const;

export const PROFILE_SECTION_PATHS: Record<
  Exclude<ProfileSection, 'diveEquipment'>,
  string
> = {
  basicPersonalInfo: '/profile/basic-personal-info',
  travelDocuments: '/profile/travel-documents',
  diveCertificate: '/profile/dive-certificate',
};

export type ProfileReadinessSummary = {
  readinessPercent: number;
  filledCount: number;
  totalRequired: number;
  missingFields: RequiredFieldInfo[];
  nextSection: Exclude<ProfileSection, 'diveEquipment'> | null;
};

export const getProfileReadiness = (
  data: ProfileCompletenessData,
): ProfileReadinessSummary => {
  const requiredFields = READINESS_SECTIONS.flatMap(
    section => PROFILE_SECTION_FIELDS[section],
  );
  const missingFields = requiredFields
    .filter(field => !isProfileFieldFilled(data[field]))
    .map(field => ({
      fieldName: field,
      message: `Missing required ${PROFILE_FIELD_LABELS[field]}`,
    }));

  const totalRequired = requiredFields.length;
  const filledCount = totalRequired - missingFields.length;
  const readinessPercent =
    totalRequired === 0 ? 100 : Math.round((100 * filledCount) / totalRequired);

  let nextSection: Exclude<ProfileSection, 'diveEquipment'> | null = null;
  for (const section of READINESS_SECTIONS) {
    if (!isSectionComplete(data, section)) {
      nextSection = section as Exclude<ProfileSection, 'diveEquipment'>;
      break;
    }
  }

  return {
    readinessPercent,
    filledCount,
    totalRequired,
    missingFields,
    nextSection,
  };
};
